import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Package, Loader2, RefreshCw, AlertTriangle, Check, Plus, Copy, Trash2, Save,
  ChevronUp, ChevronDown, Sparkles,
} from 'lucide-react';
import {
  apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult, PackContainer, PackFileNode,
} from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { loadPackPalette, PalettePickEntry } from '@/components/config/packPalette';
import { SearchPicker, PickerOption } from '@/components/config/SearchPicker';
import { ApplyReport, ContainerScope, useProjectContainers } from '@/components/config/AllContainers';
import {
  ENCHANT_IDS, LOOTBOX_ANIMATION, LootEntry, LootFile, LootFormat, LootItemDef, MAX_COUNT, NG_EXPECTED_LOOTBOXES,
  RARITIES, RewardKind, entriesOf, idOf, kindOf, normalizeEntry,
} from './lootFormat';
import styles from '@/pages/config/LootboxPage.module.scss';

// ============================================================
// Éditeur d'un type de fichier de récompenses (box à clés ou lootbox), commun à tous les containers
// ============================================================
// Le fichier est un JSON { Id, <tableau>: [...], <annexes> } où chaque entrée porte un Weight, une
// RarityId et UNE récompense : Item (Amount, Enchants, DisplayName, Damage, Lore), Money ou
// Experience. Les clés inconnues de chaque entrée sont préservées telles quelles : le moteur peut en
// ajouter sans que le panel les efface.
//
// Pas de choix de container : la liste réunit les fichiers de ce type de TOUS les containers du
// projet, par Id (son nom de fichier peut varier d'un container à l'autre). Chacun s'ouvre depuis le
// premier container (par id) qui le porte, et l'enregistrement écrit les clés gérées dans CHAQUE
// container du projet qui porte le même Id. Créer un fichier le crée dans chaque container qui a le
// mod (à côté des fichiers du même type, sinon dans le dossier du mod).
// ============================================================

function dirOf(path: string): string {
  return path.split('/').slice(0, -1).join('/');
}

/**
 * Un fichier dans chaque container du projet : le fichier de référence ouvert par la page, et
 * ailleurs le fichier qui porte le même tableau et le même Id. Sans Id, seul le nom du fichier
 * permet de le retrouver.
 */
function fileQuery(format: LootFormat, refId: string, path: string, id: string | undefined): ConfigSectionsQuery {
  const base = {
    keys: format.keys,
    requiredKey: format.arrayKey,
    requiredKind: 'array' as const,
    sourceContainerId: refId,
    sourcePath: path,
  };
  return id
    ? { ...base, fileNames: ['.json'], matchKey: 'Id', matchValue: id }
    : { ...base, fileNames: [`/${path.split('/').pop() ?? path}`] };
}

/** Fichiers trouvés dans un container. */
interface Scan { container: PackContainer; entries: PackFileNode[] }

/**
 * Un fichier de la liste, réuni sur tous les containers du projet : rassemblé par son Id (à défaut
 * par son nom de fichier), ouvert depuis le premier container (par id) qui le porte.
 */
interface FileItem {
  key: string;
  id?: string;
  /** Nom du fichier dans le container d'ouverture. */
  name: string;
  /** Chemin du fichier dans le container d'ouverture. */
  path: string;
  /** Container d'ouverture : la référence de ce fichier. */
  containerId: string;
  /** Containers qui portent ce fichier, dans l'ordre stable. */
  carriers: string[];
}

/** Réunit les scans (containers dans l'ordre stable) en une liste, triée par nom. */
function mergeScans(scans: Array<{ container: PackContainer; files: Array<{ node: PackFileNode; id?: string }> }>): FileItem[] {
  const byKey = new Map<string, FileItem>();
  /** Containers déjà comptés pour chaque fichier. */
  const seen = new Map<string, Set<string>>();
  for (const { container, files } of scans) {
    for (const { node, id } of files) {
      let key = id ? `id:${id}` : `file:${node.name.toLowerCase()}`;
      // Deux fichiers du même container pour un même Id : les deux restent visibles.
      if (seen.get(key)?.has(container.id)) key = `${key}@${container.id}:${node.path}`;
      const item = byKey.get(key);
      if (item) item.carriers.push(container.name);
      else byKey.set(key, { key, id, name: node.name, path: node.path, containerId: container.id, carriers: [container.name] });
      seen.set(key, (seen.get(key) ?? new Set<string>()).add(container.id));
    }
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
}

interface LootFileEditorProps {
  format: LootFormat;
  /** Texte d'aide propre au type, sous la barre d'actions. */
  intro?: ReactNode;
  /** Bloc ajouté sous l'en-tête du fichier ouvert (les clés d'une box). Reçoit l'Id ENREGISTRÉ. */
  renderExtras?: (savedId: string | undefined) => ReactNode;
}

export function LootFileEditor({ format, intro, renderExtras }: LootFileEditorProps) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const { containers, loaded } = useProjectContainers();
  const arrayKey = format.arrayKey;
  const isLootbox = format.kind === 'lootbox';
  const [scans, setScans] = useState<Scan[]>([]);
  /** Dossier listé directement (repli quand la recherche ne trouve rien). */
  const [forced, setForced] = useState<string | undefined>(undefined);
  const [customDir, setCustomDir] = useState('');
  const [files, setFiles] = useState<FileItem[]>([]);
  /** Fichier ouvert : son container d'ouverture et son chemin y servent de référence. */
  const [selected, setSelected] = useState<FileItem | null>(null);
  const [box, setBox] = useState<LootFile | null>(null);
  /** Id tel que lu : c'est lui qui retrouve le fichier dans les autres containers, même renommé. */
  const [loadedId, setLoadedId] = useState<string | undefined>(undefined);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [orig, setOrig] = useState('');
  /** Entrées relues à l'ancien format Enchant, réécrites en Enchants au prochain enregistrement. */
  const [legacyEnchants, setLegacyEnchants] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [openDetails, setOpenDetails] = useState<number | null>(null);
  /** Dernières lectures lancées : une réponse plus ancienne est ignorée. */
  const listSeq = useRef(0);
  const openSeq = useRef(0);

  // MÊME liste que les catalogues Blocks / Items du projet actif (blocks inclus : une box en
  // distribue, cf IsBlock), avec la vignette de chaque entrée. Saisie libre toujours possible.
  useEffect(() => {
    loadPackPalette(activeGameId).then(setPalette).catch(() => setPalette([]));
  }, [activeGameId]);

  const itemOptions = useMemo<PickerOption[]>(
    () => palette.map((p) => ({
      value: p.id,
      label: p.id,
      hint: p.kind === 'block' ? 'block' : 'item',
      thumbnailPath: p.path,
      groupId: p.groupId,
    })),
    [palette],
  );

  /**
   * Trouve les fichiers en CHERCHANT côté serveur, dans chaque container du projet, les .json dont
   * la racine porte le tableau du type (ou les .json d'un dossier indiqué). Deviner les dossiers et
   * leur casse depuis le front affichait « dossier introuvable » à tort. La liste réunit les
   * fichiers de tous les containers, par Id : un fichier présent dans un seul serveur reste éditable.
   */
  const loadFiles = useCallback(async (forcedDir?: string): Promise<FileItem[] | null> => {
    const seq = ++listSeq.current;
    ++openSeq.current; // une ouverture en cours ne doit pas rouvrir un fichier de l'ancienne liste
    setLoading(true);
    setSelected(null);
    setBox(null);
    setInspection(null);
    const found = await Promise.all(containers.map(async (c): Promise<Scan> => {
      if (forcedDir) {
        const r = await apiService.getContainerTree(c.id, forcedDir);
        return { container: c, entries: (r.data?.entries ?? []).filter((e) => !e.dir && e.name.toLowerCase().endsWith('.json')) };
      }
      const r = await apiService.searchContainerFiles(c.id, {
        name: '.json', contains: `"${arrayKey}"`, jsonKey: arrayKey, jsonKind: 'array', max: 60,
      });
      return { container: c, entries: r.data?.entries ?? [] };
    }));
    if (seq !== listSeq.current) return null;
    // L'Id de chaque fichier : c'est lui qui rassemble un même fichier d'un container à l'autre.
    const withIds = await Promise.all(found.map(async (s) => ({
      container: s.container,
      files: await Promise.all(s.entries.map(async (node) => {
        const raw = await apiService.getContainerFileRaw(s.container.id, node.path);
        try {
          return { node, id: raw == null ? undefined : idOf(JSON.parse(raw) as LootFile) };
        } catch {
          return { node, id: undefined }; // illisible ici : retrouvé par son nom de fichier
        }
      })),
    })));
    if (seq !== listSeq.current) return null;
    const items = mergeScans(withIds);
    setScans(found);
    setFiles(items);
    setForced(forcedDir);
    setLoading(false);
    return items;
  }, [containers, arrayKey]);

  useEffect(() => {
    setStatus(null);
    setReport(null);
    if (!loaded) {
      // Changement de projet : rien de l'ancien projet ne reste affiché.
      ++listSeq.current;
      setScans([]);
      setFiles([]);
      setSelected(null);
      setBox(null);
      setInspection(null);
      return;
    }
    void loadFiles();
  }, [loaded, loadFiles]);

  /** Ouvre un fichier depuis son container d'ouverture, et compare les autres containers sur ce fichier. */
  const open = async (item: FileItem) => {
    const seq = ++openSeq.current;
    setLoading(true);
    setStatus(null);
    setReport(null);
    setOpenDetails(null);
    // L'Id d'abord : c'est lui qui retrouve le fichier dans les autres containers.
    const raw = await apiService.getContainerFileRaw(item.containerId, item.path);
    let id: string | undefined;
    try {
      id = raw == null ? undefined : idOf(JSON.parse(raw) as LootFile);
    } catch {
      id = undefined; // le serveur, plus tolérant (commentaires), dira ce qu'il en est
    }
    const r = await apiService.inspectContainerConfig(fileQuery(format, item.containerId, item.path, id));
    if (seq !== openSeq.current) return;
    setLoading(false);
    if (!r.data?.success || !r.data.sections) {
      setStatus({ ok: false, msg: r.data?.message ?? r.error ?? 'Lecture impossible.' });
      return;
    }
    const sections = r.data.sections as LootFile;
    // Ancien format d'enchantement relu, puis réécrit au format canonique à l'enregistrement.
    let converted = 0;
    const entries = entriesOf(sections, format).map((e) => {
      const n = normalizeEntry(e);
      if (n.converted) converted++;
      return n.entry;
    });
    const next: LootFile = { ...sections, [arrayKey]: entries };
    // Nombre de tirages : écrit explicitement (1 par défaut) dès le prochain enregistrement.
    if (isLootbox && typeof next.Count !== 'number') next.Count = 1;
    setBox(next);
    setOrig(JSON.stringify(next));
    setLegacyEnchants(converted);
    setLoadedId(id);
    setSelected(item);
    setInspection(r.data);
  };

  /** Dossiers distincts des résultats : la recherche balaie tout le container. */
  const dirs = useMemo(() => [...new Set(files.map((f) => dirOf(f.path)))], [files]);

  const loots = useMemo(() => entriesOf(box, format), [box, format]);
  const totalWeight = useMemo(() => loots.reduce((s, e) => s + (Number(e.Weight) || 0), 0), [loots]);
  const perRarity = useMemo(() => {
    const m = new Map<string, number>();
    loots.forEach((e) => {
      const r = e.RarityId ?? '—';
      m.set(r, (m.get(r) ?? 0) + (Number(e.Weight) || 0));
    });
    return m;
  }, [loots]);

  const setEntries = (fn: (list: LootEntry[]) => LootEntry[]) => {
    setBox((prev) => (prev ? { ...prev, [arrayKey]: fn([...entriesOf(prev, format)]) } : prev));
  };

  const mutate = (i: number, patch: (e: LootEntry) => LootEntry) => {
    setEntries((list) => {
      list[i] = patch({ ...list[i] });
      return list;
    });
  };

  const mutateItem = (i: number, patch: (item: LootItemDef) => LootItemDef) => {
    mutate(i, (x) => ({ ...x, Item: patch({ ...x.Item }) }));
  };

  const setKind = (i: number, kind: RewardKind) => {
    mutate(i, (e) => {
      const out: LootEntry = { ...e };
      delete out.Money;
      delete out.Experience;
      delete out.Item;
      if (kind === 'money') out.Money = 1000;
      else if (kind === 'xp') out.Experience = 5;
      else out.Item = { Identifier: '', Amount: 1 };
      return out;
    });
  };

  const addRow = () => {
    setEntries((list) => [...list, { Weight: 100, RarityId: 'common', Item: { Identifier: '', Amount: 1 } }]);
  };

  const duplicate = (i: number) => {
    setEntries((list) => {
      list.splice(i + 1, 0, JSON.parse(JSON.stringify(list[i])) as LootEntry);
      return list;
    });
  };

  const remove = (i: number) => setEntries((list) => list.filter((_, k) => k !== i));

  const move = (i: number, delta: number) => {
    setEntries((list) => {
      const j = i + delta;
      if (j < 0 || j >= list.length) return list;
      [list[i], list[j]] = [list[j], list[i]];
      return list;
    });
  };

  const dirty = !!box && JSON.stringify(box) !== orig;
  const count = typeof box?.Count === 'number' ? box.Count : NaN;
  const countValid = !isLootbox || (Number.isInteger(count) && count >= 1 && count <= MAX_COUNT);

  /**
   * Crée un fichier vide dans CHAQUE container du projet qui a le mod (à côté des fichiers du même
   * type, sinon dans son dossier PremiumMod, ou le dossier indiqué), puis l'ouvre. Un container qui
   * porte déjà cet Id n'est pas touché.
   */
  const createFile = async (preset?: { slug: string; id: string }) => {
    if (dirty && !window.confirm(`Créer ${format.noun === 'box' ? 'une box' : 'une lootbox'} abandonnera les modifications non enregistrées. Continuer ?`)) return;
    const raw = preset?.slug ?? window.prompt('Nom du fichier (sans .json)', `nouvelle_${format.noun}`);
    if (!raw) return;
    const slug = raw.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (!slug) { setStatus({ ok: false, msg: 'Nom invalide (a-z, 0-9, _, -).' }); return; }
    const fileName = `${slug}.json`;
    if (files.some((f) => f.name.toLowerCase() === fileName)) {
      setStatus({ ok: false, msg: `${fileName} existe déjà.` });
      return;
    }
    // Espace de noms du fichier ouvert s'il y en a un, sinon celui du projet : modifiable.
    const current = idOf(box);
    const ns = current && current.includes(':') ? current.split(':')[0] : activeGameId;
    const id = preset?.id
      ?? (window.prompt(`Id de la ${format.noun} (référencé par le jeu, le même dans chaque container)`, `${ns}:${slug}`) ?? '').trim();
    if (!id) {
      setStatus({ ok: false, msg: "L'Id est obligatoire : c'est lui qui retrouve le fichier dans chaque container." });
      return;
    }
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sections: Record<string, unknown> = { Id: id, [arrayKey]: [] };
    if (isLootbox) sections.Count = 1;
    const res = await apiService.applyContainerConfig({
      fileNames: ['.json'],
      keys: format.keys,
      requiredKey: arrayKey,
      requiredKind: 'array',
      matchKey: 'Id',
      matchValue: id,
      sections,
      createFileName: fileName,
      createDir: customDir.trim() || undefined,
    });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Création impossible' });
      return;
    }
    const result = res.data;
    if (result.entries.some((e) => e.status === 'CREATED')) {
      const list = await loadFiles(forced);
      const item = list?.find((e) => e.id === id) ?? list?.find((e) => e.name.toLowerCase() === fileName);
      if (item) await open(item);
    }
    // Après l'ouverture, qui efface le rapport précédent.
    setReport(result);
  };

  /**
   * Écrit les clés gérées dans chaque container du projet qui porte ce fichier (retrouvé par l'Id
   * lu, même si la page vient de le changer), puis dit qui a reçu quoi.
   */
  const save = async () => {
    if (!box || !selected) return;
    if (!countValid) {
      setStatus({ ok: false, msg: `Nombre de tirages : entier de 1 à ${MAX_COUNT}.` });
      return;
    }
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sections: Record<string, unknown> = {};
    if (box.Id !== undefined) sections.Id = box.Id;
    sections[arrayKey] = entriesOf(box, format);
    if (isLootbox) sections.Count = box.Count;
    const res = await apiService.applyContainerConfig({ ...fileQuery(format, selected.containerId, selected.path, loadedId), sections });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (!res.data.success) return;
    setOrig(JSON.stringify(box));
    setLegacyEnchants(0);
    // Renommé : c'est désormais sous son nouvel Id qu'on le retrouve ailleurs.
    const nextId = idOf(box);
    setLoadedId(nextId);
    const r = await apiService.inspectContainerConfig(fileQuery(format, selected.containerId, selected.path, nextId));
    if (r.data) setInspection(r.data);
  };

  const reload = () => {
    if (dirty && !window.confirm('Recharger abandonnera les modifications non enregistrées. Continuer ?')) return;
    setReport(null);
    void loadFiles(customDir.trim() || undefined);
  };

  /** Containers qui portent des fichiers de ce type, et ceux qui n'en ont aucun. */
  const withFiles = scans.filter((s) => s.entries.length > 0).length;
  const withoutFiles = scans.filter((s) => s.entries.length === 0).map((s) => s.container.name);
  /** Lootbox attendues sur NationsGlory et encore absentes des containers. */
  const missingExpected = isLootbox && activeGameId === 'nationsglory' && loaded && !loading && !forced
    ? NG_EXPECTED_LOOTBOXES.filter((id) => !files.some((f) => f.id === id))
    : [];

  return (
    <>
      <div className={styles.header}>
        <div className={styles.muted}>{intro}</div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={reload} disabled={!loaded || loading}>
            {loading ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={styles.secondaryBtn} onClick={() => void createFile()} disabled={!loaded || containers.length === 0 || saving}
            title={`Crée le fichier dans chaque container du projet qui a le mod (${format.folder})`}>
            <Plus size={14} /> {format.kind === 'box' ? 'Créer une box' : 'Créer une lootbox'}
          </button>
          <button className={styles.primaryBtn} onClick={save} disabled={!dirty || saving || !countValid}
            title="Écrit le fichier dans tous les containers du projet qui le portent (même Id)">
            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </div>

      {loaded && containers.length === 0 && (
        <div className={styles.error}>
          <AlertTriangle size={14} /> Aucun container pour ce projet : déclare-le dans Paramètres généraux → Containers.
        </div>
      )}

      {withFiles > 0 && (
        <p className={styles.muted}>
          {withFiles > 1
            ? `Liste réunie depuis les ${withFiles} containers du projet qui portent des ${format.nounPlural} : chacune s'ouvre depuis le premier container (par id) qui la porte.`
            : `Liste lue dans le seul container du projet qui porte des ${format.nounPlural}.`}
          {withoutFiles.length > 0 && ` Sans ${format.noun}, donc jamais modifiés ici : ${withoutFiles.join(', ')}.`}
        </p>
      )}

      {missingExpected.length > 0 && (
        <div className={styles.warnBox}>
          <AlertTriangle size={14} />
          <span>
            Lootbox attendues sur NationsGlory et absentes des containers : {missingExpected.join(', ')}. Elles arrivent
            vides avec la release de NationsGloryBE-World-Container ; tu peux aussi les créer ici (il faut le dossier
            data/mods/PremiumMod dans le container).{' '}
            {missingExpected.map((id) => (
              <button key={id} className={styles.secondaryBtn} disabled={saving || containers.length === 0}
                onClick={() => void createFile({ slug: id.split(':').pop() ?? id, id })}>
                <Plus size={13} /> {id}
              </button>
            ))}
          </span>
        </div>
      )}

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      <div className={styles.split}>
        <aside className={styles.listCol}>
          <div className={styles.listHead}>
            {/* Les résultats peuvent venir de plusieurs dossiers : afficher un seul chemin serait faux. */}
            <span>{dirs.length === 1 ? dirs[0] : dirs.length > 1 ? `${dirs.length} dossiers` : 'rien trouvé'}</span>
            <span className={styles.muted}>{files.length}</span>
          </div>
          {files.length === 0 ? (
            <div className={styles.listEmpty}>
              <p>
                {loading
                  ? 'Recherche…'
                  : `Aucun fichier contenant une liste « ${arrayKey} » dans les containers du projet.`}
              </p>
              <p className={styles.muted}>
                Recherche faite dans chaque container (fichiers .json). Synchronise un container qui vient
                d'être déclaré, crée {format.kind === 'box' ? 'une box' : 'une lootbox'} ou liste directement un dossier :
              </p>
              <div className={styles.dirRow}>
                <input className={styles.input} value={customDir} onChange={(e) => setCustomDir(e.target.value)}
                  placeholder={format.folder} />
                <button className={styles.secondaryBtn} onClick={() => void loadFiles(customDir.trim())} disabled={!customDir.trim()}>
                  Chercher
                </button>
              </div>
            </div>
          ) : (
            files.map((f) => {
              // Fichier absent d'une partie des containers : dire lesquels le portent.
              const partial = f.carriers.length < withFiles;
              return (
                <button key={f.key} title={`${f.path} (${f.carriers.join(', ')})`}
                  className={`${styles.row} ${selected?.key === f.key ? styles.rowActive : ''}`}
                  onClick={() => {
                    if (dirty && !window.confirm('Ouvrir un autre fichier abandonnera les modifications non enregistrées. Continuer ?')) return;
                    void open(f);
                  }}>
                  <Package size={15} className={styles.rowIcon} />
                  <span className={styles.rowName}>{f.name.replace(/\.json$/i, '')}</span>
                  {partial && <span className={styles.rowHint}>{f.carriers.join(', ')}</span>}
                </button>
              );
            })
          )}
        </aside>

        <section className={styles.detail}>
          {!box ? (
            <div className={styles.detailEmpty}>
              {loading ? <Loader2 size={26} className={styles.spin} /> : <Sparkles size={26} />}
              <p>{loading ? 'Lecture…' : `Choisis une ${format.noun} à gauche.`}</p>
            </div>
          ) : (
            <>
              {inspection && (
                <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath}
                  labelOf={(k) => format.labels[k] ?? k}
                  // Sans Id, l'enregistrement n'en écrit pas et n'en retire pas ailleurs.
                  untouched={box.Id === undefined ? ['Id'] : []} />
              )}
              <div className={styles.boxHead}>
                <label className={styles.field}>
                  <span className={styles.label}>Id de la {format.noun}</span>
                  <input className={styles.input} value={box.Id ?? ''}
                    onChange={(e) => setBox({ ...box, Id: e.target.value })}
                    placeholder={format.idPlaceholder} />
                </label>
                {isLootbox && (
                  <label className={`${styles.field} ${styles.fieldNarrow}`}>
                    <span className={styles.label}>Tirages (Count)</span>
                    <input className={styles.input} type="number" min={1} max={MAX_COUNT}
                      value={Number.isNaN(count) ? '' : count}
                      onChange={(e) => setBox({ ...box, Count: e.target.value === '' ? undefined : Number(e.target.value) })} />
                  </label>
                )}
                <div className={styles.totals}>
                  <span><strong>{loots.length}</strong> entrée(s)</span>
                  <span><strong>{totalWeight}</strong> de poids total</span>
                  {[...perRarity.entries()].map(([r, w]) => (
                    <span key={r} className={styles.rarityTotal}>{r} : {w}</span>
                  ))}
                </div>
              </div>

              {!countValid && (
                <div className={styles.error}><AlertTriangle size={14} /> Nombre de tirages : entier de 1 à {MAX_COUNT}.</div>
              )}
              {legacyEnchants > 0 && (
                <p className={styles.muted}>
                  {legacyEnchants} entrée(s) lue(s) à l'ancien format « Enchant » (id, power) : affichées et réécrites
                  au format « Enchants » (Identifier, Level) au prochain enregistrement.
                </p>
              )}

              {renderExtras?.(loadedId)}

              <div className={styles.tableHead}>
                <span>Rareté</span>
                <span>Poids</span>
                <span>Chance</span>
                <span>Type</span>
                <span>Récompense</span>
                <span>Flags</span>
                <span />
              </div>

              {loots.map((e, i) => {
                const kind = kindOf(e);
                const weight = Number(e.Weight) || 0;
                const rarityWeight = perRarity.get(e.RarityId ?? '—') ?? 0;
                const globalPct = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
                const rarityPct = rarityWeight > 0 ? (weight / rarityWeight) * 100 : 0;
                const enchants = e.Item?.Enchants ?? [];
                const detailsCount = enchants.length + (e.Item?.DisplayName ? 1 : 0) + (e.Item?.Lore?.length ? 1 : 0)
                  + (e.Item?.Damage ? 1 : 0);
                const animation = isLootbox ? LOOTBOX_ANIMATION[e.RarityId ?? ''] : undefined;
                return (
                  <div key={i} className={styles.rowEdit}>
                    <div className={styles.cells}>
                      <span className={styles.rarityCell}>
                        <input className={styles.input} list={`loot-rarities-${format.kind}`} value={e.RarityId ?? ''}
                          onChange={(ev) => mutate(i, (x) => {
                            // La rareté décide de l'index côté mod : un ancien RarityIndex ne doit pas la contredire.
                            const out: LootEntry = { ...x, RarityId: ev.target.value };
                            delete out.RarityIndex;
                            return out;
                          })} />
                        {isLootbox && (
                          <em title="Animation d'ouverture jouée pour cette rareté">
                            {animation ? `animation ${animation}` : 'rareté inconnue'}
                          </em>
                        )}
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} value={e.Weight ?? 0}
                          onChange={(ev) => mutate(i, (x) => ({ ...x, Weight: Number(ev.target.value) }))} />
                      </span>
                      <span className={styles.chance} title="Part du poids total / part au sein de la rareté">
                        {globalPct.toFixed(2)}%
                        <em>{rarityPct.toFixed(1)}% de « {e.RarityId ?? '—'} »</em>
                      </span>
                      <span>
                        <select className={styles.input} value={kind} onChange={(ev) => setKind(i, ev.target.value as RewardKind)}>
                          <option value="item">Item</option>
                          <option value="money">Argent</option>
                          <option value="xp">Expérience</option>
                        </select>
                      </span>
                      <span className={styles.reward}>
                        {kind === 'money' && (
                          <input className={styles.input} type="number" min={0} value={e.Money ?? 0}
                            onChange={(ev) => mutate(i, (x) => ({ ...x, Money: Number(ev.target.value) }))} />
                        )}
                        {kind === 'xp' && (
                          <input className={styles.input} type="number" min={0} value={e.Experience ?? 0}
                            onChange={(ev) => mutate(i, (x) => ({ ...x, Experience: Number(ev.target.value) }))} />
                        )}
                        {kind === 'item' && (
                          <>
                            <span className={styles.picker}>
                              <SearchPicker
                                groupId={palette[0]?.groupId ?? ''}
                                value={e.Item?.Identifier ?? ''}
                                options={itemOptions}
                                showThumbnails
                                placeholder="paladium:paladium_ingot"
                                onChange={(v) => mutateItem(i, (it) => ({ ...it, Identifier: v }))}
                              />
                            </span>
                            <input className={`${styles.input} ${styles.amount}`} type="number" min={1}
                              value={e.Item?.Amount ?? ''} placeholder="1"
                              onChange={(ev) => {
                                const v = ev.target.value;
                                mutateItem(i, (it) => {
                                  if (v === '') delete it.Amount; else it.Amount = Number(v);
                                  return it;
                                });
                              }} />
                            <button className={styles.enchantBtn} onClick={() => setOpenDetails(openDetails === i ? null : i)}
                              title="Nom affiché, dégâts, lore et enchantements de l'objet">
                              Détails {detailsCount > 0 ? `(${detailsCount})` : ''}
                            </button>
                          </>
                        )}
                      </span>
                      <span className={styles.flags}>
                        <label><input type="checkbox" checked={!!e.IsTool}
                          onChange={(ev) => mutate(i, (x) => {
                            const out = { ...x };
                            if (ev.target.checked) out.IsTool = true; else delete out.IsTool;
                            return out;
                          })} /> outil</label>
                        <label><input type="checkbox" checked={!!e.IsBlock}
                          onChange={(ev) => mutate(i, (x) => {
                            const out = { ...x };
                            if (ev.target.checked) out.IsBlock = true; else delete out.IsBlock;
                            return out;
                          })} /> block</label>
                      </span>
                      <span className={styles.rowActions}>
                        <button className={styles.iconBtn} title="Monter" onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                        <button className={styles.iconBtn} title="Descendre" onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                        <button className={styles.iconBtn} title="Dupliquer" onClick={() => duplicate(i)}><Copy size={13} /></button>
                        <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={() => remove(i)}><Trash2 size={13} /></button>
                      </span>
                    </div>

                    {openDetails === i && kind === 'item' && (
                      <div className={styles.enchants}>
                        <div className={styles.detailsGrid}>
                          <label className={styles.field}>
                            <span className={styles.label}>Nom affiché (DisplayName)</span>
                            <input className={styles.input} value={e.Item?.DisplayName ?? ''} placeholder="nom de l'objet par défaut"
                              onChange={(ev) => mutateItem(i, (it) => {
                                if (ev.target.value === '') delete it.DisplayName; else it.DisplayName = ev.target.value;
                                return it;
                              })} />
                          </label>
                          <label className={`${styles.field} ${styles.fieldNarrow}`}>
                            <span className={styles.label}>Dégâts (Damage)</span>
                            <input className={styles.input} type="number" min={0} value={e.Item?.Damage ?? ''} placeholder="0"
                              onChange={(ev) => mutateItem(i, (it) => {
                                if (ev.target.value === '') delete it.Damage; else it.Damage = Number(ev.target.value);
                                return it;
                              })} />
                          </label>
                          <label className={styles.field}>
                            <span className={styles.label}>Lore (une ligne par entrée)</span>
                            <textarea className={styles.input} rows={2} value={(e.Item?.Lore ?? []).join('\n')}
                              onChange={(ev) => mutateItem(i, (it) => {
                                const lines = ev.target.value.split('\n');
                                if (lines.length === 1 && lines[0] === '') delete it.Lore; else it.Lore = lines;
                                return it;
                              })} />
                          </label>
                        </div>
                        <span className={styles.label}>Enchantements (Enchants)</span>
                        {enchants.length === 0 && <span className={styles.muted}>Aucun enchantement.</span>}
                        {enchants.map((en, k) => (
                          <div key={k} className={styles.enchantRow}>
                            <input className={styles.input} list={`loot-enchants-${format.kind}`} value={en.Identifier} placeholder="minecraft:unbreaking"
                              onChange={(ev) => mutateItem(i, (it) => {
                                const list = [...(it.Enchants ?? [])];
                                list[k] = { ...list[k], Identifier: ev.target.value };
                                return { ...it, Enchants: list };
                              })} />
                            <input className={`${styles.input} ${styles.amount}`} type="number" min={1} value={en.Level}
                              onChange={(ev) => mutateItem(i, (it) => {
                                const list = [...(it.Enchants ?? [])];
                                list[k] = { ...list[k], Level: Number(ev.target.value) };
                                return { ...it, Enchants: list };
                              })} />
                            <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Retirer"
                              onClick={() => mutateItem(i, (it) => {
                                const list = (it.Enchants ?? []).filter((_, z) => z !== k);
                                if (list.length === 0) delete it.Enchants; else it.Enchants = list;
                                return it;
                              })}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                        <button className={styles.secondaryBtn}
                          onClick={() => mutateItem(i, (it) => ({
                            ...it,
                            Enchants: [...(it.Enchants ?? []), { Identifier: 'minecraft:unbreaking', Level: 1 }],
                          }))}>
                          <Plus size={13} /> Ajouter un enchantement
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className={styles.footerActions}>
                <button className={styles.secondaryBtn} onClick={addRow}><Plus size={14} /> Ajouter une entrée</button>
                <span className={styles.muted}>
                  {dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}
                </span>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Listes d'autocomplétion partagées par toutes les lignes (une par onglet : les deux restent montés) */}
      <datalist id={`loot-rarities-${format.kind}`}>
        {RARITIES.map((r) => <option key={r} value={r} />)}
      </datalist>
      <datalist id={`loot-enchants-${format.kind}`}>
        {ENCHANT_IDS.map((id) => <option key={id} value={id} />)}
      </datalist>
    </>
  );
}
